import {
  Button,
  Card,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  Switch,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import NotesIcon from "@mui/icons-material/Notes";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FormTextField from "components/FormFields/FormTextField";
import FormDatePicker from "components/FormFields/FormDatePicker";
import dayjs from "dayjs";
import { formatLocalDateTime, getFormattedDate } from "utils/date";
import { DATE_FORMAT } from "static/date";
import SubmitButton from "components/Button/SubmitButton";
import { useSaveChfScreeningNotesMutation } from "app/store/transmission/api/transmissionNotesApi";
import { showErrorToast, showSuccessToast } from "utils/toast";
import { RN_OBSERVATIONS, RN_OBSERVATIONS_FIELDS } from "static/constants";
import { useDispatch } from "react-redux";
import { selectedReport } from "app/store/transmission/slice/transmissionSlice";

const ChfScreening = ({ reportDetail, isActiveOrSingleTab }) => {
  const dispatch = useDispatch();
  const [saveChfScreeningNotes] = useSaveChfScreeningNotesMutation();
  const defaultRnObservationValues = {
    cardiologist: "",
    sign_out_date: null,
    inform_patient_to_call: "",
  };

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [chfScreeningEnabled, setChfScreeningEnabled] = useState(false);
  const [selectedRnObservations, setSelectedRnObservations] = useState([]);
  const [remarks, setRemarks] = useState("");
  const [rnObservationsValues, setRnObservationsValues] = useState(
    defaultRnObservationValues
  );

  useEffect(() => {
    setChfScreeningValues(reportDetail?.chf_screening || {});
  }, [reportDetail?.chf_screening]);

  const setChfScreeningValues = (chfScreeningDetails) => {
    const { enabled, screening_symptoms, follow_up_info, remarks } =
      chfScreeningDetails;

    setChfScreeningEnabled(enabled || false);
    setSelectedRnObservations(screening_symptoms || []);
    setRnObservationsValues(follow_up_info || defaultRnObservationValues);
    setRemarks(remarks || "");
  };

  const handleObservationChange = (observation) => (e) => {
    const { checked } = e.target;

    setSelectedRnObservations(
      (prev) =>
        checked
          ? [...prev, observation] // add when checked
          : prev.filter((item) => item !== observation) // remove when unchecked
    );
  };

  const handleRnObservationValueChange = (value, key) => {
    setRnObservationsValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleChfScreeningSave = async () => {
    try {
      setIsSubmitting(true);

      const payload = {
        reportId: reportDetail?.reportId,
        chfScreening: {
          enabled: chfScreeningEnabled,
          screening_symptoms: selectedRnObservations,
          follow_up_info: rnObservationsValues,
          remarks,
        },
      };

      const response = await saveChfScreeningNotes(payload);
      if (response.error) {
        throw response;
      }

      showSuccessToast(response?.data?.message);
      dispatch(
        selectedReport({
          id: reportDetail?._id,
          isLegacyReport: reportDetail?.legacy ?? true,
          reportDetail: {
            ...reportDetail,
            chf_screening: payload?.chfScreening,
          },
        })
      );
    } catch ({ error }) {
      showErrorToast(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelChfScreeningNotes = () => {
    setChfScreeningValues(reportDetail?.chf_screening || {});
  };

  const renderCHFDisabledContent = () => {
    return (
      <>
        <span style={{ fontSize: 14, fontWeight: 500 }}>
          No heart failure symptoms present
        </span>
        <p style={{ fontWeight: 400, color: "#757575" }} className="mt-1">
          Education on CHF diet and warning signs provided with instructions to
          call cardiologist as needed
        </p>
      </>
    );
  };

  const renderCHFScreeningContent = () => {
    return (
      <>
        <p style={{ fontSize: 14, fontWeight: 500 }}>
          RN CHF screening call for possible heart failure
        </p>
        <p style={{ fontSize: 12, fontWeight: 400 }}>
          Over the last 7 days has the patient experienced{" "}
        </p>

        <div className="mt-2">
          {RN_OBSERVATIONS.map((observation) => (
            <p>
              <FormControlLabel
                label={observation}
                control={
                  <Checkbox
                    disabled={!isActiveOrSingleTab}
                    onChange={handleObservationChange(observation)}
                    checked={selectedRnObservations.includes(observation)}
                  />
                }
              />
            </p>
          ))}
        </div>

        <TextField
          fullWidth
          multiline
          minRows={3}
          maxRows={10}
          name="remarks"
          label="Remarks"
          disabled={!isActiveOrSingleTab}
          value={remarks}
          onChange={(e) => {
            setRemarks(e.target.value);
          }}
          className="mt-2 mb-2"
          inputProps={{ maxLength: 225 }}
        />

        {(selectedRnObservations.length > 0 || remarks?.trim()?.length > 0) && (
          <div
            className="mt-2"
            style={{
              background: "#E9F3FA",
              borderRadius: 4,
              padding: "10px 12px",
            }}
          >
            <Grid container>
              <Grid xs={1}>
                <InfoOutlinedIcon color="info" />
              </Grid>
              <Grid xs={11}>
                <span
                  style={{ fontSize: 14, fontWeight: 500, color: "#2196F3" }}
                >
                  Cardiologist Follow-up Information
                </span>
                <p style={{ fontSize: 12, fontWeight: 400, color: "#2196F3" }}>
                  Please complete the fields below
                </p>
              </Grid>
            </Grid>

            <div className="mt-2">
              {RN_OBSERVATIONS_FIELDS.map((field) => (
                <>
                  {field.type === "date" ? (
                    <FormDatePicker
                      name={field.label}
                      label={field.label}
                      maxDate={dayjs(new Date())}
                      disabled={!isActiveOrSingleTab}
                      sx={{
                        borderRadius: 1,
                        marginTop: "8px",
                        backgroundColor: "#fff",
                      }}
                      value={
                        rnObservationsValues[field.key]
                          ? dayjs(rnObservationsValues[field.key])
                          : null
                      }
                      onChange={(date) => {
                        const formattedDate = getFormattedDate(
                          date,
                          DATE_FORMAT.DEFAULT
                        );
                        handleRnObservationValueChange(
                          date ? formattedDate : null,
                          field.key
                        );
                      }}
                    />
                  ) : (
                    <FormTextField
                      fullWidth
                      margin="none"
                      name={field?.label}
                      label={field.label}
                      inputProps={{ maxLength: 50 }}
                      disabled={!isActiveOrSingleTab}
                      sx={{
                        borderRadius: 1,
                        marginTop: "8px",
                        backgroundColor: "#fff",
                      }}
                      value={rnObservationsValues[field.key]}
                      onChange={(e) => {
                        const value = e.target.value;
                        handleRnObservationValueChange(value, field.key);
                      }}
                    />
                  )}
                </>
              ))}
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <Card
      style={{
        boxShadow: "none",
        border: "1px solid #E0E0E0",
        marginBottom: 12,
      }}
    >
      <Grid container className="d-flex align-items-center p-2">
        <Grid item xs={1}>
          <NotesIcon className="mr-2" />
        </Grid>
        <Grid item xs={8} style={{ flex: 1, maxWidth: "none", minWidth: 0 }}>
          <span style={{ fontSize: "14px", color: "#444444", fontWeight: 500 }}>
            CHF Screening
          </span>
        </Grid>

        <Grid item xs={3} style={{ flex: "0 0 auto", maxWidth: "none" }}>
          <FormControlLabel
            label="Enable"
            labelPlacement="start"
            onChange={(e) => {
              setChfScreeningEnabled(!chfScreeningEnabled);
            }}
            control={
              <Switch
                color="secondary"
                name="activeDevice"
                checked={chfScreeningEnabled}
                disabled={!isActiveOrSingleTab}
              />
            }
          />
        </Grid>
      </Grid>
      <Divider className="mb-2" />
      <div className="p-3">
        {chfScreeningEnabled
          ? renderCHFScreeningContent()
          : renderCHFDisabledContent()}
      </div>

      <div className="process-buttons__wrapper p-3">
        <div className="d-flex justify-between ">
          <div
            className="view-pdf__notes-signature"
            style={{ color: "#757575", fontSize: 12 }}
          >
            {reportDetail?.chf_screening?.updated_by?.name && (
              <>
                <span>{reportDetail?.chf_screening?.updated_by?.name}</span>
                &nbsp;•&nbsp;
              </>
            )}
            {reportDetail?.chf_screening?.updated_at && (
              <span>
                {formatLocalDateTime(reportDetail?.chf_screening?.updated_at)}
              </span>
            )}
          </div>

          {isActiveOrSingleTab && (
            <div
              className="d-flex align-items-center flex-end"
              style={{ gap: "10px" }}
            >
              <Button
                onClick={handleCancelChfScreeningNotes}
                className="view-pdf__button"
                variant="outlined"
                color="secondary"
                disabled={isSubmitting}
              >
                Cancel
              </Button>

              <SubmitButton
                handleClick={() => handleChfScreeningSave()}
                className="view-pdf__button"
                variant="contained"
                color="secondary"
                isLoading={isSubmitting}
                disabled={
                  isSubmitting ||
                  (chfScreeningEnabled &&
                    !remarks?.trim()?.length &&
                    !selectedRnObservations.length)
                }
              />
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

export default ChfScreening;
